import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';

let maskSeq = 0;

/**
 * Dims everything except `rect` (viewport coordinates). Implemented as one
 * SVG with a mask — a huge box-shadow repaints the whole viewport on every
 * frame; a masked rect does not. The dim never blocks pointer events; the
 * optional hotspot is a real button over the cut-out for `click` steps.
 */
@Component({
  selector: 'prep-tour-spotlight',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'prep-tour-spotlight' },
  template: `
    <svg class="prep-tour-spotlight__svg" aria-hidden="true" focusable="false">
      <defs>
        <mask [attr.id]="maskId" maskUnits="userSpaceOnUse" x="0" y="0" width="100%" height="100%">
          <rect x="0" y="0" width="100%" height="100%" fill="#fff" />
          <rect *ngIf="rect as r" class="prep-tour-spotlight__cut" [class.prep-tour-spotlight__cut--animated]="!reducedMotion"
            [attr.x]="r.left" [attr.y]="r.top" [attr.width]="r.width" [attr.height]="r.height"
            [attr.rx]="radius" fill="#000" />
        </mask>
      </defs>
      <rect x="0" y="0" width="100%" height="100%" [attr.fill]="dimColor" [attr.mask]="'url(#' + maskId + ')'" />
      <rect *ngIf="rect as r" class="prep-tour-spotlight__ring" [class.prep-tour-spotlight__cut--animated]="!reducedMotion"
        [attr.x]="r.left" [attr.y]="r.top" [attr.width]="r.width" [attr.height]="r.height"
        [attr.rx]="radius" fill="none" stroke="#1976d2" stroke-width="2" />
    </svg>
    <button *ngIf="hotspot && rect as r" type="button" class="prep-tour-spotlight__hotspot"
      [style.left.px]="r.left" [style.top.px]="r.top" [style.width.px]="r.width" [style.height.px]="r.height"
      [attr.aria-label]="hotspotLabel" (click)="hotspotClick.emit()"></button>
  `,
  styles: [`
    .prep-tour-spotlight { position: fixed; inset: 0; display: block; pointer-events: none; }
    .prep-tour-spotlight__svg { width: 100%; height: 100%; }
    .prep-tour-spotlight__cut--animated {
      transition: x .35s cubic-bezier(.4,0,.2,1), y .35s cubic-bezier(.4,0,.2,1),
        width .35s cubic-bezier(.4,0,.2,1), height .35s cubic-bezier(.4,0,.2,1);
    }
    .prep-tour-spotlight__hotspot {
      position: absolute; pointer-events: auto; cursor: pointer; border: 0; border-radius: 6px;
      background: transparent; padding: 0;
    }
    .prep-tour-spotlight__hotspot:focus-visible { outline: 2px solid #1976d2; outline-offset: 2px; }
    @media (prefers-reduced-motion: reduce) { .prep-tour-spotlight__cut--animated { transition: none; } }
  `],
})
export class PrepTourSpotlightComponent {
  /** Viewport rect to keep clear; null dims nothing (centred steps). */
  @Input() rect: DOMRect | null = null;
  @Input() radius = 6;
  /** 0.2–0.7 (default 0.45). */
  @Input() set opacity(v: number) { this.dimColor = `rgba(15, 23, 42, ${Math.min(0.9, Math.max(0, v))})`; }
  @Input() hotspot = false;
  @Input() hotspotLabel = '';
  @Input() reducedMotion = false;
  @Output() hotspotClick = new EventEmitter<void>();

  readonly maskId = `prep-tour-mask-${maskSeq++}`;
  dimColor = 'rgba(15, 23, 42, 0.45)';
}
